import * as React from "react";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  CssBaseline,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  alpha,
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@mui/material";
import {
  Add as AddIcon,
  NotificationsNone as NotificationsIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";
import AppNavbar from '../../../components/AppNavbar';
import Header from '../../../components/Header';
import SideMenu from '../../../components/SideMenu';
import AppTheme from '../../../shared-theme/AppTheme';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../../dashboard/theme/customizations';
import { StyledCellHeader } from "../../../components/StyledCellHeader";
import CreatePortfolioDialog from './createportafoliodialog';
import { useNavigate } from 'react-router-dom';
import AccessDeniedBox from '../../../components/AccessDeniedBox';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

// --- Mock data ---
const KPI = [
  { label: "Total Portafolios", value: "125" },
  { label: "Total Financiado", value: "$5.25M" },
  { label: "Total Moros", value: "$150,000", color: "error.main" },
  { label: "Portafolio con Mayor Balance", value: "Portfolio Alpha", subtitle: true },
];

const rows = [
  { id: 1, name: "Portfolio Alpha", loans: 35, balance: 1500000, delinquency: 2.5, officer: "Sarah Chen" },
  { id: 2, name: "Portfolio Beta", loans: 28, balance: 1200000, delinquency: 1.8, officer: "David Lee" },
  { id: 3, name: "Portfolio Gamma", loans: 22, balance: 950000, delinquency: 3.1, officer: "Emily Wong" },
];

const columns = [
  { field: "name", headerName: "Nombre del Portafolio", flex: 1, minWidth: 180 },
  { field: "loans", headerName: "# de Préstamos", width: 130, type: "number" },
  {
    field: "balance",
    headerName: "Total Balance",
    flex: 0.8,
    minWidth: 160,
    valueFormatter: ({ value }) => value.toLocaleString("en-US", { style: "currency", currency: "USD" }),
  },
  {
    field: "delinquency",
    headerName: "Tasa de Morosidad",
    width: 160,
    valueFormatter: ({ value }) => `${value}%`,
    renderCell: (params) => (
      <Chip
        size="small"
        label={`${params.row.delinquency}%`}
        color={params.row.delinquency >= 3 ? "error" : params.row.delinquency <= 2 ? "success" : "warning"}
        variant="outlined"
      />
    ),
  },
  { field: "officer", headerName: "Responsible Officer", flex: 0.8, minWidth: 180 },
  {
    field: "actions",
    headerName: "Actions",
    width: 150,
    sortable: false,
    filterable: false,
    renderCell: () => (
      <Stack direction="row" spacing={1}>
        <IconButton size="small" color="primary"><VisibilityIcon fontSize="small" /></IconButton>
        <IconButton size="small"><EditIcon fontSize="small" /></IconButton>
        <IconButton size="small" color="error"><DeleteOutlineIcon fontSize="small" /></IconButton>
      </Stack>
    ),
  },
];

// --- Tiny bar chart (no external libs) ---
function TinyBar({ label, value, max = 100 }) {
  return (
    <Box sx={{ display: "grid", gridTemplateRows: "1fr auto", gap: 1, alignItems: "end" }}>
      <Box sx={{ height: 180, display: "flex", alignItems: "flex-end" }}>
        <Box sx={{ width: 48, height: `${Math.round((value / max) * 100)}%`, bgcolor: "primary.main", borderRadius: 1 }} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>{label}</Typography>
    </Box>
  );
}

function Distribution({ portfolios = [] }) {
  // Use real portfolio data for the distribution chart
  const data = portfolios.slice(0, 6).map(portfolio => ({
    label: portfolio.nombre?.substring(0, 8) || 'N/A',
    v: portfolio.monto_total || 0
  }));
  
  const max = data.length > 0 ? Math.max(...data.map((d) => d.v)) : 100;
  
  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>Distribución de Portafolio</Typography>
        {data.length > 0 ? (
          <Grid container spacing={3} alignItems="end" justifyContent="center">
            {data.map((d) => (
              <Grid key={d.label} item>
                <TinyBar label={d.label} value={d.v} max={max} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            No hay datos de portafolios disponibles
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}


function KPICard({ label, value, color }) {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h4" sx={{ mt: 0.5, color: color ? color : undefined }}>{value}</Typography>
      </CardContent>
    </Card>
  );
}

export default function LoanPortfoliosPage(props) {
  const [query, setQuery] = React.useState("");
  const navigate = useNavigate();
  const [portfolios, setPortfolios] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [accessDenied, setAccessDenied] = React.useState(false);
  React.useEffect(() => {
    fetchPortfolios();
  }, []);
  
  const handleOpenCreateDialog = () => {
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  async function fetchPortfolios(){
    try {
      setLoading(true);
      const response = await fetch(process.env.REACT_APP_SERVER_URL+`/api/portafolios/${localStorage.getItem('idcompany')}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany'),
        },
      });
      
      if (!response.ok) {
        throw new Error('Error al cargar los portafolios');
      }
      
      const data = await response.json();
      setPortfolios(data);
      setAccessDenied(false);
    } catch (err) {
      setAccessDenied(true);
      console.error('Error fetching portfolios:', err);
    } finally {
      setLoading(false);
    }
  }

  // Calculate KPIs from real data
  const calculateKPIs = () => {
    const totalPortfolios = portfolios.length;
    const totalFinanciado = portfolios.reduce((sum, p) => sum + parseFloat(p.monto_total || 0), 0);
    const totalMoros = portfolios.reduce((sum, p) => sum + parseFloat(p.monto_en_mora || 0), 0);
    const portfolioWithMaxBalance = portfolios.reduce((max, p) => 
      (p.monto_total || 0) > (max?.monto_total || 0) ? p : max, null
    );

    return [
      { label: "Total Portafolios", value: totalPortfolios.toString() },
      { label: "Total Financiado", value: `$${totalFinanciado.toLocaleString()}`, color: "success.main" },
      { label: "Total Moros", value: `$${totalMoros.toLocaleString()}`, color: "error.main" },
      { label: "Portafolio con Mayor Balance", value: portfolioWithMaxBalance?.nombre || "N/A", subtitle: true },
    ];
  };

  // Filter portfolios based on search query
  const filteredPortfolios = portfolios.filter(portfolio =>
    portfolio.nombre?.toLowerCase().includes(query.toLowerCase()) ||
    portfolio.portafolio_id?.toLowerCase().includes(query.toLowerCase())
  );

  const kpis = calculateKPIs();

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        {/* Main content */}
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: 'center',
              mx: 3,
              pb: 5,
              mt: { xs: 8, md: 0 },
            }}
          >
            <Header parent={"Gestión Comercial"} child={"Portafolio de Préstamos"} />
            {!accessDenied ? 
            <Container maxWidth={false} sx={{ px: 5, py: 5 }}>
              <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', fontFamily: 'Inter, "Noto Sans", sans-serif' }}>
                <Box maxWidth="lg" mx="auto" py={4}>
                  {/* Title + CTA */}
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between" sx={{ mb: 3 }}>
                    <Typography variant="h4" fontWeight={700}>Portafolio de Préstamos</Typography>
                    <Button variant="contained" startIcon={<AddIcon />} color='primarybutton' sx={{fontWeight:"bold"}} onClick={handleOpenCreateDialog}>Nuevo Portafolio</Button>
                  </Stack>      

                  {/* KPIs */}
                  <Grid container spacing={3} sx={{ mb: 1 }}>
                    {kpis.map((k) => (
                      <Grid key={k.label} size={{ xs: 12, sm: 6, md: 3 }}>
                        <KPICard label={k.label} value={k.value} color={k.color} />
                      </Grid>
                    ))}
                  </Grid>

                  <Distribution portfolios={portfolios} />

                  {/* Search */}
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent sx={{ pb: 1 }}>
                      <OutlinedInput
                        fullWidth
                        placeholder="Buscar portafolios por nombre..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        startAdornment={<InputAdornment position="start"><SearchIcon /></InputAdornment>}
                      />
                    </CardContent>
                    <Divider />
                    {/* Table */}
                    <Box sx={{ height: 420 }}>
                      {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                          <Typography>Cargando portafolios...</Typography>
                        </Box>
                      ) : error ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                          <Typography color="error">Error: {error}</Typography>
                        </Box>
                      ) : (
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <StyledCellHeader>Nombre</StyledCellHeader>
                                <StyledCellHeader>Préstamos</StyledCellHeader>
                                <StyledCellHeader>Balance</StyledCellHeader>
                                <StyledCellHeader>Sucursal</StyledCellHeader>
                                <StyledCellHeader>Acciones</StyledCellHeader>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredPortfolios.length > 0 ? (
                                filteredPortfolios.map((row) => (
                                  <TableRow key={row.id}>
                                    <TableCell>
                                      <Stack>
                                        <Typography variant="body2" fontWeight={600}>{row.nombre}</Typography>
                                        <Typography variant="caption" color="text.secondary">{row.portafolio_id}</Typography>
                                      </Stack>
                                    </TableCell>
                                    <TableCell>{row.total_prestamos || 0}</TableCell>
                                    <TableCell>${(row.monto_total || 0).toLocaleString()}</TableCell>
                                    <TableCell>{row.sucursal_nombre || 'N/A'}</TableCell>
                                    <TableCell> 
                                      <Stack direction="row" spacing={1}>
                                        <IconButton size="small" color="primary" onClick={()=>{navigate(`/gestion_comercial/portafolio/detalle/${row.id}`)}}><VisibilityIcon fontSize="small" /></IconButton>
                                        <IconButton size="small"><EditIcon fontSize="small" /></IconButton>
                                        <IconButton size="small" color="error"><DeleteOutlineIcon fontSize="small" /></IconButton>
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                ))
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={5} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                      No se encontraron portafolios
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                    <Divider />
                    <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography variant="body2" color="text.secondary">
                        Mostrando {filteredPortfolios.length} de {portfolios.length} portafolios
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button 
                          variant="outlined" 
                          onClick={fetchPortfolios}
                          disabled={loading}
                        >
                          Actualizar
                        </Button>
                      </Stack>
                    </Box>
                  </Card>
                </Box>
              </Box>
            </Container>
            :
            <Box>
            <br></br>
            <br></br>
            <AccessDeniedBox
              title={'Acceso denegado'}
              message={'No tienes permisos para ver esta sección de Portafolio de Préstamos. Contacta a tu administrador para solicitar acceso o verifica tus roles y permisos.'}
            />
            </Box>
            }
          </Stack>
        </Box>
      </Box>
      <CreatePortfolioDialog 
        open={createDialogOpen} 
        onClose={handleCloseCreateDialog} 
      />
    </AppTheme>
  );
}
